/**
 * IvaSelector — Tarjeta de IVA para formularios de CxC y CxP.
 *
 * El usuario digita la BASE en el campo "Monto"; aquí elige si la factura lleva
 * IVA y la tarifa, y se muestra el desglose Base / IVA / Total en la moneda de
 * la factura. El backend recibe `monto` = base + `tasa_iva` + `iva_incluido`.
 *
 * Props:
 *   ivaIncluido          → boolean
 *   tasaIva              → número (%, ej. 13)
 *   onIvaIncluidoChange  → (bool) => void
 *   onTasaChange         → (number) => void
 *   base                 → número (monto base en la moneda seleccionada)
 *   moneda               → 'CRC' | 'USD'
 */
import React from "react";
import { TARIFAS_IVA, IVA_MODOS, calcularIva } from "../shared/iva";

const fmtN = (n) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

export function IvaSelector({
  ivaIncluido,
  tasaIva,
  onIvaIncluidoChange,
  onTasaChange,
  // `base` es el monto DIGITADO (base si modo=SUMAR, total si modo=INCLUIDO)
  base = 0,
  moneda = "CRC",
  modo = "SUMAR",
  onModoChange,
}) {
  const aplica = !!ivaIncluido;
  const { base: baseCalc, iva, total } = calcularIva(base, tasaIva, aplica, modo);
  const simbolo = moneda === "USD" ? "$" : "₡";

  return (
    <div
      style={{
        marginTop: 14,
        border: "1px solid var(--border-card)",
        borderRadius: 10,
        overflow: "hidden",
        background: "rgba(255,255,255,0.02)",
      }}
    >
      {/* Cabecera */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "9px 14px",
          borderBottom: "1px solid var(--border-card)",
          background: "rgba(249,115,22,0.04)",
        }}
      >
        <i className="fa-solid fa-receipt" style={{ color: "#f97316", fontSize: "0.7rem" }} />
        <span
          style={{
            fontSize: "0.67rem",
            fontWeight: 700,
            color: "var(--text-muted)",
            letterSpacing: "0.6px",
            textTransform: "uppercase",
          }}
        >
          IVA (Impuesto al Valor Agregado)
        </span>
      </div>

      {/* Cuerpo */}
      <div style={{ padding: "13px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Check + tarifa */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              cursor: "pointer",
              fontSize: "0.85rem",
              color: "var(--text-secondary)",
              fontWeight: 500,
              margin: 0,
            }}
          >
            <input
              type="checkbox"
              checked={aplica}
              onChange={(e) => onIvaIncluidoChange(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: "#f97316", cursor: "pointer" }}
            />
            Aplicar IVA
          </label>

          <div style={{ width: 1, height: 24, background: "var(--border-card)", flexShrink: 0 }} />

          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: "0.73rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
              Tarifa:
            </span>
            <select
              value={tasaIva}
              disabled={!aplica}
              onChange={(e) => onTasaChange(Number(e.target.value))}
              className="form-select"
              style={{
                width: "auto",
                minWidth: 160,
                opacity: aplica ? 1 : 0.5,
                cursor: aplica ? "pointer" : "not-allowed",
              }}
            >
              {TARIFAS_IVA.map((tarifa) => (
                <option key={tarifa.valor} value={tarifa.valor}>
                  {tarifa.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Modo de IVA: Sumar vs Incluido */}
        {aplica && onModoChange && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.73rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
              Modo:
            </span>
            <div style={{ display: "flex", background: "rgba(0,0,0,0.25)", borderRadius: 8, padding: 3 }}>
              {IVA_MODOS.map((opt) => (
                <button
                  key={opt.valor}
                  type="button"
                  title={opt.hint}
                  onClick={() => onModoChange(opt.valor)}
                  style={{
                    padding: "5px 14px",
                    borderRadius: 6,
                    fontSize: "0.8rem",
                    fontWeight: modo === opt.valor ? 700 : 400,
                    background: modo === opt.valor ? "rgba(249,115,22,0.2)" : "transparent",
                    border: `1px solid ${modo === opt.valor ? "rgba(249,115,22,0.45)" : "transparent"}`,
                    color: modo === opt.valor ? "#f97316" : "var(--text-muted)",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Desglose */}
        {Number(base) > 0 && (
          <div
            style={{
              background: "rgba(0,0,0,0.15)",
              borderRadius: 7,
              padding: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <Row label="Base gravable" value={`${simbolo}${fmtN(baseCalc)}`} muted />
            <Row
              label={`IVA (${aplica ? Number(tasaIva) : 0}%)`}
              value={`${simbolo}${fmtN(iva)}`}
              muted
            />
            <div style={{ height: 1, background: "var(--border-card)", margin: "2px 0" }} />
            <Row
              label="Total a pagar"
              value={`${simbolo}${fmtN(total)}`}
              strong
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, muted, strong }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span
        style={{
          fontSize: strong ? "0.85rem" : "0.78rem",
          color: strong ? "var(--text-primary)" : "var(--text-muted)",
          fontWeight: strong ? 700 : 400,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "monospace",
          fontWeight: strong ? 700 : 600,
          fontSize: strong ? "0.92rem" : "0.85rem",
          color: strong ? "#f97316" : "var(--text-secondary)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export default IvaSelector;
